import { useEffect, useState } from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { Dismiss16Regular } from "@fluentui/react-icons";
import { FuiButton } from "@/components/fui-control";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Inspector } from "@/features/inspector/Inspector";
import { InsightsPanel } from "@/features/insights/InsightsPanel";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { StatusBar } from "@/features/status-bar/StatusBar";
import { Toolbar } from "@/features/toolbar/Toolbar";
import { Workspace } from "@/features/workspace/Workspace";
import { cn } from "@/lib/utils";
import { useProjectStore, type SidePanel } from "@/store/project-store";

const useStyles = makeStyles({
  header: {
    display: "flex",
    alignItems: "center",
    height: "44px",
    minHeight: "44px",
    paddingInline: tokens.spacingHorizontalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground3,
    flexShrink: 0,
    gap: tokens.spacingHorizontalXS,
    boxSizing: "border-box",
  },
  tabs: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    display: "flex",
    "& > button": {
      flex: 1,
      justifyContent: "center",
      maxWidth: "none",
    },
  },
  wbs: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "56px",
  },
  close: {
    flexShrink: 0,
    minWidth: "28px",
    width: "28px",
    maxWidth: "28px",
    minHeight: "28px",
    height: "28px",
    maxHeight: "28px",
  },
});

export function ProjectSchedulePage() {
  const styles = useStyles();
  const sidePanel = useProjectStore((s) => s.ui.sidePanel);
  const setUI = useProjectStore((s) => s.setUI);
  const focusId = useProjectStore((s) => s.focusId);
  const focusSched = useProjectStore((s) => (focusId ? s.derived.schedule.tasks.get(focusId) : undefined));

  // 일정 화면을 벗어나면 패널을 닫아, 다시 들어왔을 때 항상 닫힌 상태로 시작한다.
  useEffect(() => {
    return () => {
      const ui = useProjectStore.getState().ui;
      if (ui.sidePanel) useProjectStore.getState().setUI({ sidePanel: null });
    };
  }, []);

  // 닫히는 애니메이션 중 찰나에 내용이 사라지지 않도록 마지막 활성 패널 유지
  const [lastPanel, setLastPanel] = useState<SidePanel>("insights");
  useEffect(() => {
    if (sidePanel) setLastPanel(sidePanel);
  }, [sidePanel]);

  const activePanel = sidePanel ?? lastPanel;

  return (
    <TooltipProvider delayDuration={400}>
      <div className="flex h-full min-h-0 min-w-[960px] flex-col">
        {/* 상단 툴바 + 메인 워크스페이스와 사이드바가 수평 배치되는 컨테이너 */}
        <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
          {/* 좌측 영역: 툴바 + 캔버스(Workspace) */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <Toolbar />
            <div className="flex min-h-0 min-w-0 flex-1">
              <Workspace />
            </div>
          </div>

          {/* 우측 인스펙터 레일: 툴바 높이(44px)부터 바닥까지 통째로 덮는 Full-Height 기둥 */}
          <aside
            aria-hidden={!sidePanel}
            className={cn(
              "flex shrink-0 flex-col border-l border-border bg-background overflow-hidden transition-[width,opacity] duration-200 ease-out shadow-[-4px_0_16px_rgba(0,0,0,0.03)]",
              sidePanel ? "w-[320px] opacity-100" : "w-0 opacity-0 border-l-0 pointer-events-none"
            )}
          >
            <div className="flex h-full w-[320px] min-h-0 flex-col">
              {/* 상단 헤더: 좌측 툴바(h-[44px])와 정확히 1:1로 같은 높이와 하단 테두리를 가짐 */}
              <div className={styles.header}>
                <SegmentedControl
                  className={styles.tabs}
                  value={activePanel}
                  onChange={(v) => setUI({ sidePanel: v })}
                  ariaLabel="사이드 패널"
                  options={[
                    { value: "insights", label: "인사이트" },
                    {
                      value: "inspector",
                      label: (
                        <>
                          인스펙터
                          {focusSched && <span className={styles.wbs}>({focusSched.wbs})</span>}
                        </>
                      ),
                    },
                  ]}
                />
                <FuiButton
                  className={styles.close}
                  appearance="subtle"
                  density="dialog"
                  icon={<Dismiss16Regular />}
                  onClick={() => setUI({ sidePanel: null })}
                  title="패널 닫기 (⌘I)"
                  aria-label="패널 닫기"
                />
              </div>

              {/* 사이드바 본문: 연회색 백드롭(bg-background) */}
              <div className="min-h-0 flex-1 overflow-hidden">
                {activePanel === "insights" ? <InsightsPanel /> : <Inspector />}
              </div>
            </div>
          </aside>
        </div>

        <StatusBar />
      </div>
    </TooltipProvider>
  );
}
